// ============================================================
// ALBATROS — App.tsx
// Root: estado global + routing entre vistas
// ============================================================

import React, { useMemo, useState, useEffect } from 'react';
import { formatCurrency } from '../utils/currency';
import { SaleReturnViewType, ViewType } from '../types/view.types';
import { LeftColCart } from '../components/LeftColCart/LeftColCart';
import MovementsView from '../components/MovementsView/MovementsView';
import PurchaseOrdersView from '../components/OrdersView/PurchaseOrdersView';
import RestockView from '../components/RestockView/RestockView';
import { Cashier } from '../types/cashier.types';
import { BillingSelection, EMPTY_BILLING } from '../types/client.types';
import SettingsModal from '../components/SettingsModal/SettingsModal';
import { SuccessToast } from '../components/SuccessToast/SuccessToast';
import { Book, CartItem } from '../types/book.types';
import { Transaction } from '../types/transaction.types';
import './index.css';
import Layout from './Layout';
import { useCartTotals } from '../hooks/useCartTotals';
import { useBookSearch } from '../hooks/useBookSearch';
import { useInvoiceSearch } from '../hooks/useInvoiceSearch';
import { findBookByReference, getRandomBook } from '../services/BooksService';
import { ReturnModal } from '../components/ReturnModal/ReturnModal';
import { PromoCodeType } from '../types/promo.types';
import { ProcessingModal, ProcessingKind } from '../components/ProcessingModal/ProcessingModal';
import { RegisterClosureView } from '../components/RegisterClosureView/RegisterClosureView';
import { CashSession, ensureCashSession, openCashSession } from '../services/CashSessionService';
import { placeCustomerOrders } from '../services/PurchasingService';
import { ClientSelectionModal } from '../components/ClientSelectionModal/ClientSelectionModal';
import { useClients } from '../hooks/useClients';
import { Client } from '../types/client.types';
import { registerSale, registerRefund, REFUND_ROUNDING_MARGIN } from '../services/SalesService';
import {
    getTicketHistory,
    findTicket,
    getTicket,
    resolveTicketRefundAmount,
    registerTicketSale,
    registerTicketReturn,
    linkTicketInvoice,
    VARIOS_CLIENT_ID
} from '../services/TicketsService';
import { printTicket, htmlTicket } from '../services/TicketPrintService';
import { printInvoice, invoiceHtml, sendInvoiceEmail, invoiceRecipient } from '../services/DocumentsService';
import { DerTraveler } from '../types/der.types';
import { requestDer } from '../services/DerService';
import { getMyPermissions, hasAnyPermission, ARQUEO_PERMISSIONS } from '../services/PermissionsService';
import { DerModal } from '../components/DerModal/DerModal';
import { EmailPromptModal } from '../components/EmailPromptModal/EmailPromptModal';
import { DocumentViewerModal } from '../components/DocumentViewerModal/DocumentViewerModal';
import { fueraDePlazo, mensajeFueraDePlazo } from '../constants/returns.constants';

export default function App() {

    const [view, setView] = useState<ViewType>('sale');
    const [cart, setCart] = useState<CartItem[]>([]);
    // Datos de facturacion de la venta en curso. Viven aqui y no en LeftColCart
    // porque ese componente se desmonta al cambiar de seccion, y al volver a la
    // pantalla de venta el cliente ya seleccionado se perdia.
    const [billing, setBilling] = useState<BillingSelection>(EMPTY_BILLING);
    const [history, setHistory] = useState<Transaction[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // Sesion de caja abierta (la jornada actual). El fondo de apertura y lo
    // esperado en el arqueo salen de ella, no de localStorage.
    const [cashSession, setCashSession] = useState<CashSession | null>(null);

    const [scannerStatus, setScannerStatus] = useState<'idle' | 'ready' | 'processing'>('ready');

    const [selectedPromo, setSelectedPromo] = useState<PromoCodeType | null>(null);

    // Envios no implementados: el modal de direccion es solo visual, asi que no
    // se le pasa direccion y el coste de envio nunca se aplica.
    const cartTotals = useCartTotals(cart, view, null, selectedPromo);

    // Return Modal State
    const [selectedTxForReturn, setSelectedTxForReturn] = useState<Transaction | null>(null);
    /** Que esta esperando el overlay a pantalla completa; null = oculto. */
    const [processing, setProcessing] = useState<ProcessingKind | null>(null);

    // Return Cart State
    const [returnCart, setReturnCart] = useState<CartItem[]>([]);
    const [saleCart, setSaleCart] = useState<CartItem[]>([]);


    // Settings Modal State
    const [isSettingsOpen, setIsSettingsOpen] = useState(true);
    const [settingsMode, setSettingsMode] = useState<'normal' | 'logout'>('logout');
    const [settingsView, setSettingsView] = useState<'menu' | 'change_user'>('change_user');
    const [pinValue, setPinValue] = useState('');
    const [cashier, setCashier] = useState<Cashier | null>(null);
    // Acceso al arqueo y cierre de caja: lo da el permiso arqueo_tienda_read o
    // arqueo_tienda_all (por rol o por grupo) del cajero logado. Cerrado hasta
    // saberlo: si los permisos no se pueden leer, no se ofrece.
    const [canArqueo, setCanArqueo] = useState(false);
    // Cambia en cada logout: se usa como key de LeftColCart para remontarlo y
    // tirar tambien su estado interno (cliente, tax free, modales), que no vive
    // en este componente y por tanto no lo limpia resetTpvState.
    const [sessionKey, setSessionKey] = useState(0);
    const [enableCashChangeCalculator, setEnableCashChangeCalculator] = useState<boolean>(true);

    // RightColNumpad States
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [numpadValue, setNumpadValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [detailedBookId, setDetailedBookId] = useState<string | null>(null);
    const [selectedSearchSource, setSelectedSearchSource] = useState<any | null>(null);
    const [currentReturnTxId, setCurrentReturnTxId] = useState<string | null>(null);
    // La factura cargada, con las cantidades COMPRADAS. El carrito lleva las
    // cantidades A DEVOLVER (arrancan a 0), asi que hace falta guardar el
    // original aparte: es el tope de cada linea y lo que se devuelve entero.
    const [currentReturnTx, setCurrentReturnTx] = useState<Transaction | null>(null);

    // Numpad Hidden State
    const [isNumpadHidden, setIsNumpadHidden] = useState(false);

    // Toast State
    const [toastState, setToastState] = useState<{ visible: boolean; title: string; description: string; iconType?: 'success' | 'alert' }>({
        visible: false,
        title: '',
        description: '',
        iconType: 'success'
    });

    // Helper to get active cart
    const activeCart = useMemo(() => view === 'returns' ? returnCart : saleCart, [view, saleCart, returnCart]);

    const toastTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const showToast = (title: string, description: string, iconType: 'success' | 'alert' = 'success') => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToastState({ visible: true, title, description, iconType });
        toastTimeoutRef.current = setTimeout(() => setToastState(prev => ({ ...prev, visible: false })), 3000);
    };

    // Si la termica elegida en Ajustes falla (desconectada, apagada), el ticket no se
    // pierde: sale por el navegador, y aqui se avisa del motivo.
    const avisoImpresoraTermica = (motivo: string) =>
        showToast('Impresora de tickets', `${motivo} El ticket sale por el navegador.`, 'alert');

    // Visor de documentos: el ticket o la factura de un movimiento, en pantalla.
    const [viewer, setViewer] = useState<{
        kind: 'ticket' | 'invoice';
        title: string;
        subtitle: string;
        load: () => Promise<string>;
        onPrint: () => Promise<void>;
        onEmail?: () => void;
    } | null>(null);

    // Envio de la factura desde el visor: al email registrado del cliente y, si
    // no tiene, se pide uno.
    const [invoiceMail, setInvoiceMail] = useState<{ invoiceId: string; ticketNumber: string; clientName: string } | null>(null);

    // Feedback del boton de enviar del visor: loader mientras sale, "Enviado" despues.
    const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

    const sendViewerInvoice = async (invoiceId: string, ticketNumber: string, email?: string) => {
        setEmailStatus('sending');
        try {
            await sendInvoiceEmail(invoiceId, email, ticketNumber);
            setEmailStatus('sent');
            showToast('Factura enviada', `Factura enviada${email ? ` a ${email}` : ' al email del cliente'}.`, 'success');
        } catch (err) {
            setEmailStatus('idle');
            showToast('No se pudo enviar la factura', (err as any)?.message || 'Error desconocido', 'alert');
        }
    };

    const emailViewerInvoice = async (invoiceId: string, ticketNumber: string) => {
        setEmailStatus('sending');
        try {
            const { clientName, email } = await invoiceRecipient(invoiceId);
            if (email) {
                await sendViewerInvoice(invoiceId, ticketNumber, email);
            } else {
                setEmailStatus('idle');
                setInvoiceMail({ invoiceId, ticketNumber, clientName });
            }
        } catch (err) {
            setEmailStatus('idle');
            showToast('No se pudo enviar la factura', (err as any)?.message || 'Error desconocido', 'alert');
        }
    };

    // ticket_list devuelve los tickets SIN lineas: se pide el completo.
    const ticketCompleto = async (tx: Transaction): Promise<Transaction> => {
        const completo = tx.items && tx.items.length > 0 ? tx : await getTicket(tx.id);
        if (!completo) throw new Error('No se encontró el ticket.');
        return completo;
    };

    // Convertir un ticket en factura: se elige o se crea el cliente y, en cuanto
    // hay cliente, se emite la factura con las lineas y el total del ticket.
    const [convertTx, setConvertTx] = useState<Transaction | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const convertClients = useClients(!!convertTx);
    /** Selector "Solicitar DER" del selector de cliente al convertir un ticket. */
    const [convertDer, setConvertDer] = useState(false);
    /** Cliente ya elegido a la espera de los datos del viajero del DER. */
    const [convertDerAsk, setConvertDerAsk] = useState<{ tx: Transaction; client: Client } | null>(null);

    const chooseConvertClient = (tx: Transaction, client: Client) => {
        if (convertDer) {
            setConvertTx(null);
            setConvertDerAsk({ tx, client });
        } else {
            convertTicketToInvoice(tx, client);
        }
    };

    const convertTicketToInvoice = async (tx: Transaction, client: Client, traveler?: DerTraveler) => {
        if (isConverting) return;
        setIsConverting(true);
        setConvertTx(null);
        try {
            const completo = await ticketCompleto(tx);
            if (completo.invoiceId) throw new Error('El ticket ya tiene factura.');
            // Los descuentos del ticket ya llevan incluida la promo: no se repite.
            const invoiceId = await registerSale(
                completo.items,
                completo.total,
                String(client.id),
                completo.paymentMethod,
                0,
                tx.id
            );
            await linkTicketInvoice(tx.id, invoiceId);
            setHistory(prev => prev.map(h => (h.id === tx.id
                ? { ...h, invoiceId, clientId: String(client.id), clientName: client.companyName }
                : h)));
            // Igual que al cobrar: la factura ya existe, un fallo del DER solo se avisa.
            if (traveler) {
                try {
                    await requestDer(invoiceId, traveler);
                } catch (err) {
                    showToast(
                        'Factura generada, DER pendiente',
                        `Ticket ${tx.ticketNumber || tx.id} facturado. No se pudo solicitar el DER: ${(err as any)?.message || 'error desconocido'}`,
                        'alert'
                    );
                    return;
                }
            }
            showToast('Factura generada', `Ticket ${tx.ticketNumber || tx.id} facturado a ${client.companyName}.${traveler ? ' DER solicitado.' : ''}`, 'success');
        } catch (err) {
            showToast('No se pudo generar la factura', (err as any)?.message || 'Error desconocido', 'alert');
        } finally {
            setIsConverting(false);
        }
    };

    const handleViewTicket = (tx: Transaction) => {
        setViewer({
            kind: 'ticket',
            title: `Ticket ${tx.ticketNumber || tx.id}`,
            subtitle: tx.type === 'return' ? 'Devolución' : 'Venta',
            load: async () => htmlTicket(await ticketCompleto(tx)),
            onPrint: async () => printTicket(await ticketCompleto(tx), { onFallback: avisoImpresoraTermica })
        });
    };

    const handleViewInvoice = (tx: Transaction) => {
        // La factura cuelga del ticket: sin ella no hay nada que enseñar.
        if (!tx.invoiceId) {
            showToast('Sin factura', `El ticket ${tx.ticketNumber || tx.id} no tiene factura asociada.`, 'alert');
            return;
        }
        const invoiceId = tx.invoiceId;
        setViewer({
            kind: 'invoice',
            title: `Factura ${tx.invoiceNumber || invoiceId}`,
            subtitle: `Ticket ${tx.ticketNumber || tx.id}`,
            load: () => invoiceHtml(invoiceId, tx.ticketNumber || ''),
            onPrint: () => printInvoice(invoiceId, tx.ticketNumber || ''),
            onEmail: () => { emailViewerInvoice(invoiceId, tx.ticketNumber || ''); }
        });
        setEmailStatus('idle');
    };

    const handleOpenSettings = () => {
        setSettingsMode('normal');
        setSettingsView('menu');
        setIsSettingsOpen(true);
    };

    // Los datos de facturacion son de la venta, no del cajero ni de la pantalla:
    // se limpian al cerrar un cobro y al cerrar sesion.
    const clearBillingSelection = () => setBilling(EMPTY_BILLING);

    // Salir del panel lo deja en blanco: al volver se ve el numpad vacio, no lo
    // que tecleo el cajero hace dos clientes. Vale para las dos entradas, el
    // buscador y el ISBN/SKU del numpad.
    const clearEntry = () => {
        setSearchQuery('');
        setIsSearchFocused(false);
        setDetailedBookId(null);
        setSelectedSearchSource(null);
        setNumpadValue('');
        setErrorMessage(null);
    };

    // Descargar el ticket de devolucion: lo usan el boton "Cambiar Ticket", el
    // fin de una devolucion y la salida de la seccion.
    const clearReturnTicket = () => {
        setCurrentReturnTxId(null);
        setCurrentReturnTx(null);
        setCart([]);
    };

    // Cargar una factura para devolver: sus lineas entran en el carrito a 0 y el
    // cajero sube lo que devuelve. Lo usan el numpad y el buscador por id.
    const loadReturnInvoice = (invoice: Transaction) => {
        setCurrentReturnTxId(invoice.id);
        setCurrentReturnTx(invoice);
        setCart(invoice.items.map(item => ({ ...item, quantity: 0 })));
        setNumpadValue('');
        setSearchQuery('');
        setIsSearchFocused(false);
        setErrorMessage(null);
    };

    const [refreshCounter, setRefreshCounter] = useState(0);

    // La caja se abre sola al entrar un cajero si todavia no hay sesion: asi
    // todo ticket cuelga de una sesion y el arqueo lo cuenta.
    const refreshCashSession = async () => {
        try {
            setCashSession(await ensureCashSession());
        } catch (err) {
            console.error('Error cargando la sesion de caja:', err);
            showToast('Caja', (err as any)?.message || 'No se pudo cargar la sesión de caja.', 'alert');
        }
    };

    useEffect(() => {
        if (cashier) refreshCashSession();
    }, [cashier]);

    useEffect(() => {
        let mounted = true;
        const loadHistory = async () => {
            try {
                if (mounted) setIsLoadingHistory(true);
                const tickets = await getTicketHistory(cashSession ? cashSession.id : undefined);
                if (mounted) {
                    setHistory(tickets);
                }
            } catch (err) {
                console.error('Error loading history:', err);
            } finally {
                if (mounted) setIsLoadingHistory(false);
            }
        };
        // Fetch history when session starts or when navigating to views that display it
        if (view === 'movements' || view === 'register_closure' || history.length === 0) {
            loadHistory();
        }
        return () => { mounted = false; };
    }, [sessionKey, refreshCounter, cashSession && cashSession.id]);

    useEffect(() => {
        let mounted = true;
        setCanArqueo(false);
        if (!cashier) return () => { mounted = false; };
        getMyPermissions()
            .then(granted => { if (mounted) setCanArqueo(hasAnyPermission(granted, ARQUEO_PERMISSIONS)); })
            .catch(err => console.error('No se pudieron leer los permisos del cajero:', err));
        return () => { mounted = false; };
    }, [cashier]);

    // Si el cajero cambia (o pierde el permiso) con el arqueo abierto, se sale.
    useEffect(() => {
        if (!canArqueo && view === 'register_closure') setView('sale');
    }, [canArqueo, view]);

    // Cambiar de seccion cierra el buscador: el panel de resultados se va y lo
    // tecleado no reaparece al volver a la pantalla de venta.
    const handleChangeView = (next: ViewType) => {
        if (next === 'register_closure' && !canArqueo) {
            showToast('Sin permiso', 'Tu usuario no tiene acceso al arqueo de caja.', 'alert');
            return;
        }
        clearEntry();
        // Venta y devolucion comparten `cart`: si se sale de devoluciones con un
        // ticket cargado, sus lineas apareceria luego como carrito de venta.
        if (view === 'returns' && next !== 'returns') clearReturnTicket();
        setView(next);
        if (next === 'movements' || next === 'register_closure') {
            setRefreshCounter(prev => prev + 1);
        }
        // Los totales esperados del arqueo se leen al entrar, con los tickets
        // de ese momento.
        if (next === 'register_closure') refreshCashSession();
    };

    const resetTpvState = () => {
        clearBillingSelection();
        setView('sale');
        setCart([]);
        setSaleCart([]);
        setReturnCart([]);
        setSelectedPromo(null);
        clearReturnTicket();
        setSelectedTxForReturn(null);
        clearEntry();
        setIsNumpadHidden(false);
        setSessionKey(k => k + 1);
    };

    const handleOpenLogout = () => {
        resetTpvState();
        setSettingsMode('logout');
        setSettingsView('change_user');
        setIsSettingsOpen(true);
    };

    const hasStockFor = (book: Book | CartItem, wanted: number): boolean => {
        if (book.stock < wanted) {
            showToast('Sin stock', `"${book.title}": quedan ${book.stock}.`, 'alert');
            return false;
        }
        return true;
    };


    const updateQuantity = async (id: string, delta: number) => {
        if (view === 'returns') {
            // No se puede devolver mas de lo comprado, y la linea NO se borra al
            // bajar a 0: sigue siendo parte del ticket cargado.
            const originalItem = currentReturnTx?.items.find(i => i.id === id);
            const maxQty = originalItem ? originalItem.quantity : 1;
            const currentQty = cart.find(i => i.id === id)?.quantity ?? 0;
            const nextQty = Math.max(0, Math.min(maxQty, currentQty + delta));
            if (nextQty === currentQty) return;

            // Segundo tope, en dinero: el carrito entero no puede pasar de lo
            // que le queda vivo a la factura. Es solo comodidad -- quien corta
            // de verdad es handleProcessReturn -- pero evita que el cajero monte
            // el carrito entero para que se lo tumben al confirmar.
            const available = currentReturnTx?.refundableAmount;
            if (available !== undefined && nextQty > currentQty) {
                const lineUnit = originalItem
                    ? originalItem.price * (1 - (originalItem.discount || 0) / 100)
                    : 0;
                if (cartTotals.total + lineUnit * (nextQty - currentQty) > available + REFUND_ROUNDING_MARGIN) {
                    showToast(
                        'Límite de devolución',
                        `A esta factura solo le quedan ${formatCurrency(available)} por devolver.`,
                        'alert'
                    );
                    return;
                }
            }

            setCart(prev =>
                prev.map(item => item.id === id ? { ...item, quantity: nextQty } : item)
            );
            return;
        }

        if (delta > 0) {
            const item = cart.filter(i => i.id === id)[0];
            if (item && !item.selectedSource && !hasStockFor(item, item.quantity + delta)) return;
        }

        setCart(prev =>
            prev
                .map(item => item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item)
                .filter(item => item.quantity > 0)
        );
    };

    // Descuento directo de una linea: el % queda en la propia linea y de ahi lo
    // leen los totales, el cobro y el ticket. Solo en ventas: en devoluciones el
    // boton no existe, las lineas llevan el descuento con el que se vendieron.
    const applyLineDiscount = (id: string, percentage: number) => {
        if (view === 'returns') return;
        setCart(prev => prev.map(item => item.id === id ? { ...item, discount: percentage } : item));
    };

    const addToCart = async (book: Book, source?: any) => {
        const inCart = cart.filter(i => i.id === book.id)[0];
        // Un libro sin stock entra al ticket como linea de PEDIDO cuando se ha
        // elegido proveedor: no se vende del almacen, se pide al cobrar. Sin
        // proveedor (un escaneo suelto) se sigue rechazando.
        const isOrderLine = (!!source && book.stock <= 0) || !!(inCart && inCart.selectedSource);
        if (!isOrderLine && !hasStockFor(book, (inCart ? inCart.quantity : 0) + 1)) return;

        setCart(prev => {
            const existing = prev.find(i => i.id === book.id);
            if (existing) {
                return prev.map(i => i.id === book.id
                    ? { ...i, quantity: i.quantity + 1, selectedSource: source || i.selectedSource }
                    : i);
            }
            return [...prev, { ...book, quantity: 1, discount: 0, selectedSource: isOrderLine ? source : undefined }];
        });
    };

    // Catalogo real: /books_list busca por titulo, ISBN y autor (ILIKE sobre los
    // tres en books/list.avap). El escaner y la entrada manual van por el mismo
    // sitio via findBookByReference, incluido el escaneo simulado.
    const {
        books: filteredBooksForSearch,
        isLoading: isSearchLoading,
        error: searchError
    } = useBookSearch(searchQuery, view !== 'returns');

    // En devoluciones el mismo input busca facturas: lo tecleado se manda tal
    // cual como id a /invoices_get, sin pasar por books_list.
    const {
        invoice: invoiceResult,
        isLoading: isInvoiceSearchLoading,
        error: invoiceSearchError
    } = useInvoiceSearch(searchQuery, view === 'returns');

    const onNumpadPress = (val: string) => {
        setErrorMessage(null);
        if (val === 'CLR') {
            setNumpadValue('');
        } else if (val === 'DEL') {
            setNumpadValue(prev => prev.slice(0, -1));
        } else if (val === 'ENTER') {
            handleManualEntry(numpadValue);
        } else {
            // Entrada solo numérica para decimales fijos
            if (val === '.') return;
            if (numpadValue.length < 15) setNumpadValue(prev => prev + val);
        }
    };

    const handleScan = async (input: string) => {
        const cleanInput = input.trim().toUpperCase();
        if (!cleanInput) return;

        setScannerStatus('processing');

        // Perform search immediately
        if (view === 'returns') {
            // Tickets reales por id o por numero: /tickets_get y /ticket_list.
            try {
                const ticket = await findTicket(cleanInput);
                if (ticket && fueraDePlazo(ticket.timestamp)) {
                    // Tecleado o escaneado no pasa por el resultado del
                    // buscador, que es donde se bloquea el clic: aqui tambien.
                    setErrorMessage(mensajeFueraDePlazo(ticket.ticketNumber || ticket.id, ticket.timestamp));
                    setNumpadValue('');
                } else if (ticket) {
                    loadReturnInvoice(ticket);
                } else {
                    setErrorMessage(`Ticket "${cleanInput}" no encontrado`);
                    setNumpadValue('');
                }
            } catch (err) {
                setErrorMessage(err?.message || 'No se pudo consultar el ticket.');
                setNumpadValue('');
            }
        } else {
            // Catalogo real: mismo /books_list que el buscador. Vale tanto para
            // el escaner como para lo tecleado a mano en el numpad.
            try {
                const product = await findBookByReference(cleanInput);
                if (product) {
                    addToCart(product);
                    setNumpadValue('');
                    setErrorMessage(null);
                } else {
                    setErrorMessage(`Producto "${cleanInput}" no encontrado`);
                    setNumpadValue('');
                }
            } catch (err) {
                setErrorMessage(err?.message || 'No se pudo consultar el catalogo.');
                setNumpadValue('');
            }
        }

        setTimeout(() => {
            setScannerStatus('ready');
        }, 100);
    };

    const simulateRandomScan = async () => {
        if (view === 'returns') {
            const sales = history.filter(h => h.type === 'sale');
            if (sales.length > 0) {
                const randomTx = sales[Math.floor(Math.random() * sales.length)];
                handleScan(randomTx.ticketNumber || randomTx.id);
            } else {
                alert('No hay ventas registradas para simular devolución.');
            }
            return;
        }

        // Se sortea sobre el catalogo real y se pasa su referencia por el mismo
        // camino que un escaneo de verdad. Hay libros sin ISBN, y para esos
        // findBookByReference tambien acepta el id.
        setScannerStatus('processing');
        try {
            const randomBook = await getRandomBook();
            if (!randomBook) {
                setErrorMessage('El catalogo no tiene libros que escanear.');
                setScannerStatus('ready');
                return;
            }
            handleScan(randomBook.isbn || randomBook.id);
        } catch (err) {
            setErrorMessage(err?.message || 'No se pudo consultar el catalogo.');
            setScannerStatus('ready');
        }
    };

    // Teclear una referencia a mano equivale a escanearla: mismo camino contra
    // /books_list. Antes solo sacaba un toast y no buscaba nada.
    const handleManualEntry = (val: string) => handleScan(val);
    
    type ProcessingToast = {
        title: string;
        description: string;
        icon: 'success' | 'alert';
        ticketId?: string;
        ticketNumber?: string;
        invoiceId?: string;
    };

    const handleProcessPayment = async (
        method: 'cash' | 'card' | 'marcial_card' | 'gift_card' | 'invoice',
        clientInfo?: {
            client?: any;
            authorizedPerson?: any;
            generateInvoice?: boolean;
            isTaxFree?: boolean;
            /** Direccion a la que mandar la factura emitida; null/undefined = no mandarla. */
            invoiceEmail?: string | null;
            /** Viajero para solicitar el DER (devolucion de IVA, DIVA); null = no se pide. */
            der?: DerTraveler | null;
        },
        /**
         * Codigo leido en CardScanModal, para los metodos que pasan por el.
         * Se guarda en la transaccion del historial; el backend todavia no lo
         * recoge en registerTicketSale.
         */
        cardCode?: string
    ) => {
        if (cart.length === 0) return;

        // Foto del cobro antes de esperar al gateway: el escaner es un teclado
        // y puede meter lineas nuevas en el carrito mientras el ticket viaja.
        const items = [...cart];
        const total = cartTotals.total;
        const discountPercent = selectedPromo ? selectedPromo.discountPercent : 0;
        const clientId: string | undefined = clientInfo?.client?.id;

        // Las lineas de PEDIDO (libro sin stock con proveedor) generan al cobrar
        // un pedido para el cliente de la venta: sin cliente no hay para quien.
        const orderLines = items.filter(i => i.selectedSource && i.selectedSource.id);
        if (orderLines.length > 0 && !clientId) {
            showToast(
                'Cliente Requerido',
                'Venta con artículos sin stock (genera pedido). Es obligatorio seleccionar un cliente para poder contactarle.',
                'alert'
            );
            return;
        }
        if (!cashSession) await refreshCashSession();
        // El ticket se crea siempre. La factura SOLO si se ha pedido (interruptor
        // de factura, envio, tax free, DER o pago contra factura: LeftColCart lo
        // resume en generateInvoice) y SIEMPRE que haya cliente. Sin cliente, se
        // factura al de "varios".
        const invoiceClientId = clientInfo?.generateInvoice || clientId ? (clientId || VARIOS_CLIENT_ID) : null;

        // Tarjeta y regalo mantienen los 1800 ms de datafono simulado; el
        // registro real corre en paralelo, asi que el modal dura lo que tarde
        // el mas lento de los dos. El ternario ademas estrecha `method`: con un
        // booleano suelto, 'cash' e 'invoice' acabarian en un ProcessingKind
        // que no los admite.
        const terminalKind: ProcessingKind | null =
            method === 'card' || method === 'marcial_card' || method === 'gift_card'
                ? method
                : null;
        setProcessing(terminalKind || 'sale');

        // Ticket -> factura con ticket_id -> enlace. Si falla el ticket no hay
        // venta registrada; si falla la factura, el ticket ya existe.
        const registerTicketAndInvoice = async (): Promise<ProcessingToast> => {
            let ticket;
            try {
                ticket = await registerTicketSale({
                    items,
                    total,
                    paymentMethod: method,
                    clientId,
                    authorizedPersonId: clientInfo?.authorizedPerson?.id,
                    isTaxFree: clientInfo?.isTaxFree,
                    promoPct: discountPercent
                });
            } catch (err) {
                return {
                    title: 'Fallo al registrar la venta',
                    description: (err as any)?.message || 'Error desconocido',
                    icon: 'alert' as const
                };
            }

            if (!invoiceClientId) {
                return {
                    title: 'Cobro completado',
                    description: `Ticket ${ticket.number} registrado.`,
                    icon: 'success' as const,
                    ticketId: ticket.id,
                    ticketNumber: ticket.number
                };
            }

            try {
                const invoiceId = await registerSale(items, total, invoiceClientId, method, discountPercent, ticket.id);
                await linkTicketInvoice(ticket.id, invoiceId);

                // El email no condiciona la venta: la factura YA esta emitida, asi
                // que un fallo se avisa y se sigue, igual que con los pedidos a
                // proveedor y con la impresion del ticket.
                const email = clientInfo?.invoiceEmail;
                let emailNote = '';
                let emailFailed = false;
                if (email) {
                    try {
                        await sendInvoiceEmail(invoiceId, email, ticket.number);
                        emailNote = ` Enviada a ${email}.`;
                    } catch (err) {
                        emailFailed = true;
                        emailNote = ` No se pudo enviar a ${email}: ${(err as any)?.message || 'error desconocido'}.`;
                    }
                }

                // Igual que el email: la factura ya esta emitida, un fallo del DER
                // se avisa y el cajero lo repite por otro canal.
                let derNote = '';
                let derFailed = false;
                if (clientInfo?.der) {
                    try {
                        await requestDer(invoiceId, clientInfo.der);
                        derNote = ' DER solicitado.';
                    } catch (err) {
                        derFailed = true;
                        derNote = ` No se pudo solicitar el DER: ${(err as any)?.message || 'error desconocido'}`;
                    }
                }

                const pending = emailFailed || derFailed;
                return {
                    title: pending
                        ? `Cobro completado, ${[emailFailed && 'email', derFailed && 'DER'].filter(Boolean).join(' y ')} pendiente`
                        : 'Cobro completado',
                    description: `Ticket ${ticket.number} y factura ${invoiceId} emitidos.${emailNote}${derNote}`,
                    icon: (pending ? 'alert' : 'success') as 'success' | 'alert',
                    ticketId: ticket.id,
                    ticketNumber: ticket.number,
                    invoiceId
                };
            } catch (err) {
                return {
                    title: 'Ticket registrado, factura pendiente',
                    description: `Ticket ${ticket.number}: ${(err as any)?.message || 'no se pudo emitir la factura.'}`,
                    icon: 'alert' as const,
                    ticketId: ticket.id,
                    ticketNumber: ticket.number
                };
            }
        };

        // Tras el ticket, un pedido por proveedor con las lineas de PEDIDO, para
        // el cliente de la venta. Si un pedido falla, la venta ya esta cobrada y
        // registrada: se avisa en vez de deshacerla.
        const registration: Promise<ProcessingToast> = (async () => {
            const base = await registerTicketAndInvoice();
            if (!base.ticketId || orderLines.length === 0) return base;

            let placed: string[];
            try {
                placed = await placeCustomerOrders(orderLines, clientId as string, base.ticketNumber as string);
            } catch (err) {
                return {
                    ...base,
                    title: 'Cobro completado, pedido pendiente',
                    description: `${base.description} No se pudo crear el pedido a proveedor: ${(err as any)?.message || 'error desconocido'}`,
                    icon: 'alert' as const
                };
            }

            return {
                ...base,
                description: `${base.description} Pedido ${placed.join(', ')} enviado a aprobación.`
            };
        })();

        const terminal = terminalKind
            ? new Promise<void>(resolve => setTimeout(resolve, 1800))
            : Promise.resolve();

        const [result] = await Promise.all([registration, terminal]);

        setProcessing(null);

        // Sin ticket no hay venta registrada: el carrito se queda para reintentar.
        if (!result.ticketId) {
            showToast(result.title, result.description, result.icon);
            return;
        }

        const saleTransaction: Transaction = {
            id: result.ticketId,
            ticketNumber: result.ticketNumber,
            timestamp: new Date(),
            items,
            total,
            paymentMethod: method,
            type: 'sale',
            clientId,
            clientName: clientInfo?.client?.companyName,
            authorizedPersonId: clientInfo?.authorizedPerson?.id,
            authorizedPersonName: clientInfo?.authorizedPerson?.name,
            generateInvoice: !!invoiceClientId,
            isTaxFree: clientInfo?.isTaxFree,
            invoiceId: result.invoiceId,
            marcialCardCode: method === 'marcial_card' ? cardCode : undefined,
            giftCardCode: method === 'gift_card' ? cardCode : undefined
        };
        setHistory(prev => [saleTransaction, ...prev]);
        setCart([]);
        setSelectedPromo(null);
        clearBillingSelection();
        showToast(result.title, result.description, result.icon);

        // El ticket sale solo al cobrar. Si la impresion falla, la venta YA esta
        // registrada y cobrada: se avisa y se deja reimprimir desde Movimientos,
        // nunca se deshace el cobro. Sin await, para que el cajero vea el toast
        // y el carrito limpio sin esperar al spooler.
        // Con cliente la venta es siempre factura: sale la factura y no el ticket.
        // Solo si la factura no pudo emitirse se imprime el ticket.
        const impresion = clientId && result.invoiceId
            ? printInvoice(result.invoiceId, result.ticketNumber || '')
            : printTicket(saleTransaction, { onFallback: avisoImpresoraTermica });
        impresion.catch(err =>
            showToast(
                clientId && result.invoiceId ? 'Factura sin imprimir' : 'Ticket sin imprimir',
                `${(err as any)?.message || 'Error de impresión'}. Reimprímelo desde Movimientos.`,
                'alert'
            )
        );
    };

    const handleCobrarClick = () => showToast('Cobro iniciado', 'Procesando el pago de la compra...', 'success');
    const handleProcessReturn = async (txId: string, items: CartItem[], isFull: boolean) => {
        setSelectedTxForReturn(null);

        // El ticket puede venir del backend y no estar en el historial.
        const originalTx = history.find(h => h.id === txId) || currentReturnTx;
        if (!originalTx) return;

        // Ultima red: el buscador y el escaneo ya cortan, pero el ReturnModal de
        // Movimientos no pasa por ellos y un ticket cargado hace rato pudo
        // cruzar la medianoche del ultimo dia.
        if (fueraDePlazo(originalTx.timestamp)) {
            showToast(
                'Fuera de plazo',
                mensajeFueraDePlazo(originalTx.ticketNumber || originalTx.id, originalTx.timestamp),
                'alert'
            );
            return;
        }

        const returnTotal = items.reduce((sum, item) => sum + (item.price * item.quantity * (1 - item.discount / 100)), 0);

        // Quien autoriza es el backend: se le manda el importe y /ticket_refundable
        // responde 422 si supera lo que le queda vivo al ticket. El tope que se
        // cargo con el ticket no sirve para esto -- puede tener minutos, y otra
        // caja puede haber devuelto por medio.
        //
        // Es el unico filtro por el que pasan las tres entradas: DEVOLUCION
        // TOTAL del carrito y las dos del ReturnModal de Movimientos.
        //
        // `refundAmount` puede salir unos centimos por debajo de returnTotal: es
        // el ajuste del ultimo abono de una devolucion por partes. Se abona ese,
        // no lo pedido, para que el ticket quede saldado al centimo.
        let refundAmount = returnTotal;
        try {
            refundAmount = await resolveTicketRefundAmount(originalTx.id, returnTotal);
        } catch (err) {
            showToast(
                'Devolución no permitida',
                (err as any)?.message || 'No se pudo comprobar el importe disponible del ticket.',
                'alert'
            );
            return;
        }

        setProcessing('return');

        let result: ProcessingToast;
        try {
            const returned = await registerTicketReturn(originalTx.id, {
                items,
                total: refundAmount,
                paymentMethod: originalTx.paymentMethod,
                clientId: originalTx.clientId
            });

            // Si el ticket se facturo, la factura se rectifica con su abono, que
            // sigue siendo la misma cadena de facturas de siempre.
            let abonoId: string | undefined;
            let abonoError: string | undefined;
            if (originalTx.invoiceId && originalTx.clientId) {
                try {
                    abonoId = await registerRefund(
                        originalTx.invoiceId,
                        items,
                        refundAmount,
                        originalTx.clientId,
                        originalTx.paymentMethod,
                        0,
                        returned.id
                    );
                    await linkTicketInvoice(returned.id, abonoId);
                } catch (err) {
                    abonoError = (err as any)?.message || 'no se pudo emitir el abono.';
                }
            }

            result = abonoError
                ? {
                    title: 'Devolución registrada, abono pendiente',
                    description: `Ticket ${returned.number}: ${abonoError}`,
                    icon: 'alert',
                    ticketId: returned.id,
                    ticketNumber: returned.number
                }
                : {
                    title: 'Devolución procesada',
                    description: abonoId
                        ? `Ticket ${returned.number} y abono ${abonoId} emitidos sobre ${originalTx.ticketNumber || originalTx.id}.`
                        : `Ticket ${returned.number} emitido sobre ${originalTx.ticketNumber || originalTx.id}.`,
                    icon: 'success',
                    ticketId: returned.id,
                    ticketNumber: returned.number,
                    invoiceId: abonoId
                };
        } catch (err) {
            result = {
                title: 'Fallo al registrar la devolución',
                description: (err as any)?.message || 'Error desconocido',
                icon: 'alert'
            };
        }

        if (result.ticketId) {
            const returnTransaction: Transaction = {
                id: result.ticketId,
                ticketNumber: result.ticketNumber,
                timestamp: new Date(),
                items: items,
                total: refundAmount,
                paymentMethod: originalTx.paymentMethod,
                type: 'return',
                clientId: originalTx.clientId,
                clientName: originalTx.clientName,
                invoiceId: result.invoiceId,
                refundsTicketId: originalTx.id,
                refundsTicketNumber: originalTx.ticketNumber
            };
            setHistory(prev => [returnTransaction, ...prev]);
        }
        // Solo se descarga el ticket si la devolucion venia del cargado en
        // pantalla. Desde el modal de Movimientos no se toca el carrito, que
        // puede ser una venta a medias.
        if (result.ticketId && currentReturnTxId === txId) clearReturnTicket();
        if (result.ticketId) setReturnCart([]);
        setProcessing(null);
        showToast(result.title, result.description, result.icon);
    };

    return (
        <>
            <Layout
                view={view}
                setView={handleChangeView}
                onOpenSettings={handleOpenSettings}
                onOpenLogout={handleOpenLogout}
            >
                {view === 'sale' || view === 'returns' ? (
                    <LeftColCart
                        key={sessionKey}
                        cart={cart}
                        view={view}
                        currentReturnTxId={currentReturnTxId}
                        currentReturnDate={currentReturnTx ? currentReturnTx.timestamp : null}
                        onChangeTicket={clearReturnTicket}
                        onFullReturn={() => {
                            if (currentReturnTx) {
                                handleProcessReturn(currentReturnTx.id, currentReturnTx.items, true);
                            }
                        }}
                        updateQuantity={updateQuantity}
                        onApplyDiscount={applyLineDiscount}
                        onAddRecommendation={(book) => addToCart(book)}
                        simulateRandomScan={simulateRandomScan}
                        totals={cartTotals}
                        scannerStatus={scannerStatus}
                        currentCashier={cashier}
                        billing={billing}
                        setBilling={setBilling}
                        selectedPromo={selectedPromo}
                        setSelectedPromo={setSelectedPromo}
                        isNumpadHidden={isNumpadHidden}
                        onShowNumpad={() => setIsNumpadHidden(false)}
                        onBackgroundClick={() => {
                            clearEntry();
                            setIsNumpadHidden(true);
                        }}
                        handleCobrarClick={handleCobrarClick}
                        onProcessPayment={handleProcessPayment}
                        enableCashChangeCalculator={enableCashChangeCalculator}
                        numpadProps={{
                            isHidden: isNumpadHidden,
                            errorMessage,
                            setErrorMessage,
                            view: view as SaleReturnViewType,
                            numpadValue,
                            setNumpadValue,
                            searchQuery,
                            setSearchQuery,
                            isSearchFocused,
                            setIsSearchFocused,
                            detailedBookId,
                            setDetailedBookId,
                            filteredBooksForSearch,
                            isSearchLoading,
                            searchError,
                            invoiceResult,
                            isInvoiceSearchLoading,
                            invoiceSearchError,
                            onSelectInvoice: loadReturnInvoice,
                            addToCart,
                            selectedSearchSource,
                            setSelectedSearchSource,
                            onNumpadPress,
                            currentReturnTxId,
                            handleProcessReturn,
                            // El carrito de devolucion ES `cart`: `returnCart` no
                            // lo escribia nadie, asi que el boton de devolucion
                            // parcial estaba siempre deshabilitado.
                            returnCart: cart,
                            handleScan,
                            simulateRandomScan,
                            handleManualEntry,
                            isScanning: scannerStatus === 'processing',
                        }}
                        showToast={showToast}
                    />
                ) : view === 'orders' ? (
                    <PurchaseOrdersView showToast={showToast} />
                ) : view === 'restock' ? (
                    <RestockView showToast={showToast} />
                ) : view === 'movements' ? (
                    <MovementsView
                        history={history}
                        isLoading={isLoadingHistory}
                        onReprint={async (tx) => {
                            try {
                                // ticket_list devuelve los tickets SIN lineas: hay que
                                // pedir el ticket completo antes de imprimir, igual que
                                // hace onReturn aqui abajo.
                                const completo = tx.items && tx.items.length > 0 ? tx : await getTicket(tx.id);
                                if (!completo) {
                                    showToast('Reimpresión', 'No se encontró el ticket.', 'alert');
                                    return;
                                }
                                await printTicket(completo, { onFallback: avisoImpresoraTermica });
                            } catch (e) {
                                showToast('Reimpresión', 'No se pudo imprimir: ' + ((e as any)?.message || 'error desconocido'), 'alert');
                            }
                        }}
                        onViewTicket={handleViewTicket}
                        onViewInvoice={handleViewInvoice}
                        onMakeInvoice={(tx) => { if (!isConverting) { setConvertDer(false); setConvertTx(tx); } }}
                        onInvoice={async (tx) => {
                            // La factura cuelga del ticket: sin ella no hay nada que imprimir.
                            if (!tx.invoiceId) {
                                showToast('Sin factura', `El ticket ${tx.ticketNumber || tx.id} no tiene factura asociada.`, 'alert');
                                return;
                            }
                            try {
                                showToast('Impresión', `Enviando la factura de ${tx.ticketNumber || tx.id} a imprimir...`, 'success');
                                await printInvoice(tx.invoiceId, tx.ticketNumber || '');
                            } catch (err) {
                                showToast('No se pudo imprimir la factura', (err as any)?.message || 'Error desconocido', 'alert');
                            }
                        }}
                        onReturn={async (tx) => {
                            if (!tx.items || tx.items.length === 0) {
                                try {
                                    const fullTx = await getTicket(tx.id);
                                    setSelectedTxForReturn(fullTx || tx);
                                } catch (e) {
                                    showToast('Error', 'No se pudieron cargar las líneas: ' + (e as any).message, 'alert');
                                }
                            } else {
                                setSelectedTxForReturn(tx);
                            }
                        }}
                    />
                ) : view === 'register_closure' && (
                    <RegisterClosureView
                        session={cashSession}
                        onOpenSession={refreshCashSession}
                        onCloseRegister={async (leftover) => {
                            // El servidor ya cerro la caja: se vacia la pantalla
                            // y se abre la siguiente jornada, que arranca con el
                            // fondo que acaba de quedar para mañana.
                            setHistory([]);
                            setCashSession(null);
                            try {
                                setCashSession(await openCashSession());
                            } catch (err) {
                                console.error('Error abriendo la siguiente caja:', err);
                            }
                            showToast(
                                'Arqueo de Caja Completado',
                                `Caja cerrada y conciliada con éxito. Fondo de apertura para la siguiente jornada: ${formatCurrency(leftover)}.`,
                                'success'
                            );
                            setView('sale');
                        }}
                    />
                )}
            </Layout>

            <SettingsModal
                isOpen={isSettingsOpen}
                view={settingsView}
                setView={setSettingsView}
                pinValue={pinValue}
                setPinValue={setPinValue}
                isClosable={settingsMode === 'normal'}
                onClose={() => setIsSettingsOpen(false)}
                canRegisterClosure={canArqueo}
                onSelectRegisterClosure={() => {
                    handleChangeView('register_closure');
                    setIsSettingsOpen(false);
                }}
                onUserChange={(user) => {
                    setCashier(user);
                    if (settingsMode === 'logout') {
                        setIsSettingsOpen(false);
                    }
                    showToast('Operación Realizada', 'Caja actualizada e inventario sincronizado.', 'success');
                }}
                onLoginError={(msg) => showToast('Error de inicio de sesión', msg, 'alert')}
                enableCashChangeCalculator={enableCashChangeCalculator}
                onToggleCashChangeCalculator={(val) => setEnableCashChangeCalculator(val)}
            />

            <ReturnModal
                isOpen={!!selectedTxForReturn}
                transaction={selectedTxForReturn}
                onClose={() => setSelectedTxForReturn(null)}
                onConfirm={handleProcessReturn}
            />

            {viewer && (
                <DocumentViewerModal
                    kind={viewer.kind}
                    title={viewer.title}
                    subtitle={viewer.subtitle}
                    load={viewer.load}
                    onPrint={viewer.onPrint}
                    onEmail={viewer.onEmail}
                    emailStatus={emailStatus}
                    onClose={() => setViewer(null)}
                />
            )}

            {invoiceMail && (
                <EmailPromptModal
                    title="Enviar Factura por Email"
                    clientName={invoiceMail.clientName}
                    confirmLabel="ENVIAR AHORA"
                    hint="Se usará solo para este envío. La ficha del cliente no cambia."
                    onClose={() => setInvoiceMail(null)}
                    onConfirm={(email) => {
                        const { invoiceId, ticketNumber } = invoiceMail;
                        setInvoiceMail(null);
                        sendViewerInvoice(invoiceId, ticketNumber, email);
                    }}
                />
            )}

            {convertTx && (
                <ClientSelectionModal
                    onClose={() => setConvertTx(null)}
                    onSelect={(client) => chooseConvertClient(convertTx, client)}
                    onAddClient={(client) => {
                        convertClients.setClients([...convertClients.clients, client]);
                        chooseConvertClient(convertTx, client);
                    }}
                    der={{ checked: convertDer, onChange: setConvertDer }}
                    clients={convertClients.clients}
                    selectedClient={null}
                    selectedPerson={null}
                    isLoading={convertClients.isLoading}
                    error={convertClients.error}
                />
            )}

            {convertDerAsk && (
                <DerModal
                    clientName={convertDerAsk.client.companyName}
                    onClose={() => setConvertDerAsk(null)}
                    onConfirm={(traveler) => {
                        const { tx, client } = convertDerAsk;
                        setConvertDerAsk(null);
                        convertTicketToInvoice(tx, client, traveler);
                    }}
                />
            )}

            <ProcessingModal kind={processing} />


            <SuccessToast
                visible={toastState.visible}
                title={toastState.title}
                description={toastState.description}
                iconType={toastState.iconType}
            />
        </>
    );
}